/**
 * Cascaded sun shadow maps with CACHED cascades.
 *
 * The terrain is static and the sun is slow, so a cascade's depth map stays
 * valid for many frames. Each cascade is a light-space box centred on the
 * camera POSITION (not fit to the view frustum), which keeps it valid while
 * the player looks around. A cascade is re-rendered only when
 *   (a) the camera drifted more than 1/4 half-extent from its cached centre,
 *   (b) the quantised light direction stepped, or
 *   (c) caster geometry changed (worldVersion) — throttled and staggered.
 *
 * The map is padded to 1.25x the nominal half-extent so that, with the camera
 * up to 1/4 off-centre, everything within `halfExtent` of the camera is still
 * covered. The shader therefore picks a cascade by testing the shadow UV, not
 * by camera distance.
 *
 * Sampling uses a smooth tent-weighted 3x3 PCF on the unsnapped receiver
 * position, so shadow edges are soft rather than blocky.
 *
 * Assumes the default depth setup (no reversed / logarithmic depth buffer).
 */

import * as THREE from 'three';
import { WORLD_HEIGHT } from '../constants';

export interface ShadowOptions {
  /** 0 (off), 2 or 3 */
  cascades: number;
  /** e.g. 2048 */
  mapSize: number;
}

export interface ShadowUniforms {
  [name: string]: THREE.IUniform;
  uShadowMap0: THREE.IUniform<THREE.Texture | null>;
  uShadowMap1: THREE.IUniform<THREE.Texture | null>;
  uShadowMap2: THREE.IUniform<THREE.Texture | null>;
  /** world -> shadow clip-space remapped to [0,1]^3 (xy = uv, z = depth) per cascade */
  uShadowMat0: THREE.IUniform<THREE.Matrix4>;
  uShadowMat1: THREE.IUniform<THREE.Matrix4>;
  uShadowMat2: THREE.IUniform<THREE.Matrix4>;
  /** x = 1/mapSize, y = max shadow distance (world units), z = fade range, w = cascade count */
  uShadowParams: THREE.IUniform<THREE.Vector4>;
}

/** Nominal half-extents (world units around the camera) per cascade count. */
const HALF_EXTENTS_2: readonly number[] = [24, 80];
const HALF_EXTENTS_3: readonly number[] = [16, 48, 128];

/** Camera may drift this fraction of the half-extent before a cascade recentres. */
const RECENTRE_FRACTION = 0.25;
/** Map radius = halfExtent * (1 + RECENTRE_FRACTION): full coverage at max drift. */
const COVERAGE_PAD = 1 + RECENTRE_FRACTION;
/** Shadows end at this fraction of the largest half-extent... */
const MAX_DISTANCE_FRACTION = 0.9;
/** ...fading out over this fraction of that distance. */
const FADE_FRACTION = 0.12;

/** Light direction quantisation step (0.15 degrees). */
const LIGHT_STEP = (0.15 * Math.PI) / 180;
/** Below this elevation (sine) the depth range stops growing. */
const MIN_LIGHT_Y = 0.05;
/** Cap on how far towards the light casters are captured beyond the map radius. */
const MAX_CASTER_REACH = 384;
const MIN_CASTER_REACH = 16;
/** Slack behind the furthest receiver. */
const BACK_MARGIN = 8;

/** Min interval between re-renders caused ONLY by worldVersion changes (ms). */
const WORLD_THROTTLE_NEAR_MS = 100;
const WORLD_THROTTLE_FAR_MS = 300;

/** clip [-1,1] -> [0,1] */
const BIAS = new THREE.Matrix4().set(
  0.5, 0, 0, 0.5,
  0, 0.5, 0, 0.5,
  0, 0, 0.5, 0.5,
  0, 0, 0, 1,
);

// module scratch — update() must not allocate
const _camPos = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _eye = new THREE.Vector3();
const AXIS_X = new THREE.Vector3(1, 0, 0);
const AXIS_Y = new THREE.Vector3(0, 1, 0);

interface Cascade {
  target: THREE.WebGLRenderTarget;
  depth: THREE.DepthTexture;
  camera: THREE.OrthographicCamera;
  halfExtent: number;
  /** Half-width of the ortho box (padded half-extent). */
  radius: number;
  /** Snapped world-space centre the cached map was rendered around. */
  centre: THREE.Vector3;
  rendered: boolean;
  /** Quantised light direction key the cached map was rendered with. */
  keyEl: number;
  keyAz: number;
  worldVersion: number;
  lastRenderMs: number;
  /** The uShadowMatN value object. */
  matrix: THREE.Matrix4;
}

function normaliseCascades(n: number): number {
  if (!(n > 0)) return 0;
  return n >= 3 ? 3 : 2;
}

/** Maps every point outside [0,1]^3 so the shader skips a not-yet-rendered cascade. */
function setInvalid(m: THREE.Matrix4): void {
  m.set(0, 0, 0, -10, 0, 0, 0, -10, 0, 0, 0, -10, 0, 0, 0, 1);
}

export class ShadowCascades {
  readonly uniforms: ShadowUniforms;
  /** Number of cascade re-renders performed in the last update() call (for the perf HUD). */
  lastRenders = 0;

  private readonly renderer: THREE.WebGLRenderer;
  private cascades: Cascade[] = [];
  private cascadeCount = -1;
  private mapSize = 0;

  // quantised light state for the current update()
  private readonly qDir = new THREE.Vector3(0, 1, 0);
  private qEl = 0;
  private qAz = 0;

  // renderer state saved around a batch of cascade renders
  private savedTarget: THREE.WebGLRenderTarget | null = null;
  private savedCubeFace = 0;
  private savedMip = 0;
  private savedAutoClear = true;

  constructor(renderer: THREE.WebGLRenderer, opts: ShadowOptions) {
    this.renderer = renderer;
    const invalid = (): THREE.Matrix4 => {
      const m = new THREE.Matrix4();
      setInvalid(m);
      return m;
    };
    this.uniforms = {
      uShadowMap0: { value: null },
      uShadowMap1: { value: null },
      uShadowMap2: { value: null },
      uShadowMat0: { value: invalid() },
      uShadowMat1: { value: invalid() },
      uShadowMat2: { value: invalid() },
      uShadowParams: { value: new THREE.Vector4(1, 0, 0, 0) },
    };
    this.setOptions(opts);
  }

  /** Re-creates the targets (no-op when nothing changed). Uniform objects keep their identity. */
  setOptions(opts: ShadowOptions): void {
    const count = normaliseCascades(opts.cascades);
    const size = Math.max(16, Math.floor(opts.mapSize));
    if (count === this.cascadeCount && size === this.mapSize) return;

    this.disposeTargets();
    this.cascadeCount = count;
    this.mapSize = size;

    const u = this.uniforms;
    const maps = [u.uShadowMap0, u.uShadowMap1, u.uShadowMap2];
    const mats = [u.uShadowMat0, u.uShadowMat1, u.uShadowMat2];
    const extents = count === 3 ? HALF_EXTENTS_3 : HALF_EXTENTS_2;

    for (let i = 0; i < 3; i++) {
      setInvalid(mats[i].value);
      if (i >= count) continue;

      // 24-bit depth sampled as a plain sampler2D (compareFunction stays null).
      const depth = new THREE.DepthTexture(size, size, THREE.UnsignedIntType);
      depth.minFilter = THREE.NearestFilter;
      depth.magFilter = THREE.NearestFilter;
      // three always allocates a colour attachment; R8 is the cheapest one.
      const target = new THREE.WebGLRenderTarget(size, size, {
        format: THREE.RedFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        generateMipmaps: false,
        depthBuffer: true,
        stencilBuffer: false,
        depthTexture: depth,
      });

      const halfExtent = extents[i];
      const radius = halfExtent * COVERAGE_PAD;
      const camera = new THREE.OrthographicCamera(-radius, radius, radius, -radius, 0, 1);
      // We write the matrix directly so the texel snapping is exact.
      camera.matrixAutoUpdate = false;

      this.cascades.push({
        target,
        depth,
        camera,
        halfExtent,
        radius,
        centre: new THREE.Vector3(),
        rendered: false,
        keyEl: 0,
        keyAz: 0,
        worldVersion: -1,
        lastRenderMs: 0,
        matrix: mats[i].value,
      });
      maps[i].value = depth;
    }

    const maxDist = count > 0 ? extents[count - 1] * MAX_DISTANCE_FRACTION : 0;
    u.uShadowParams.value.set(1 / size, maxDist, maxDist * FADE_FRACTION, count);
  }

  /**
   * Re-render whichever cascades are stale. `lightDir` = unit vector TOWARDS the light.
   * `worldVersion` increments whenever caster geometry changed.
   */
  update(
    shadowScene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    lightDir: THREE.Vector3,
    worldVersion: number,
  ): void {
    this.lastRenders = 0;
    const cascades = this.cascades;
    const n = cascades.length;
    if (n === 0) return;

    this.quantiseLight(lightDir);
    camera.getWorldPosition(_camPos);
    const now = performance.now();

    let far = -1;
    let farScore = -1;
    for (let i = 0; i < n; i++) {
      const c = cascades[i];
      const drift = c.halfExtent * RECENTRE_FRACTION;
      const hard =
        !c.rendered ||
        c.keyEl !== this.qEl ||
        c.keyAz !== this.qAz ||
        _camPos.distanceToSquared(c.centre) > drift * drift;
      // Chunk streaming bumps worldVersion every frame for seconds: throttle.
      const soft =
        c.worldVersion !== worldVersion &&
        now - c.lastRenderMs >= (i === 0 ? WORLD_THROTTLE_NEAR_MS : WORLD_THROTTLE_FAR_MS);
      if (!hard && !soft) continue;

      // Near cascade and first-ever renders go immediately; other far
      // cascades are staggered, at most one per frame.
      if (i === 0 || !c.rendered) {
        this.renderCascade(c, shadowScene, worldVersion, now);
        continue;
      }
      const score = (hard ? 1e12 : 0) + (now - c.lastRenderMs);
      if (score > farScore) {
        farScore = score;
        far = i;
      }
    }
    if (far >= 0) this.renderCascade(cascades[far], shadowScene, worldVersion, now);

    if (this.lastRenders > 0) {
      const r = this.renderer;
      r.setRenderTarget(this.savedTarget, this.savedCubeFace, this.savedMip);
      r.autoClear = this.savedAutoClear;
      this.savedTarget = null;
    }
  }

  dispose(): void {
    this.disposeTargets();
    this.cascadeCount = -1;
    this.mapSize = 0;
    this.uniforms.uShadowParams.value.set(1, 0, 0, 0);
  }

  private disposeTargets(): void {
    const u = this.uniforms;
    for (const c of this.cascades) {
      c.target.dispose();
      c.depth.dispose();
    }
    this.cascades.length = 0;
    u.uShadowMap0.value = null;
    u.uShadowMap1.value = null;
    u.uShadowMap2.value = null;
    setInvalid(u.uShadowMat0.value);
    setInvalid(u.uShadowMat1.value);
    setInvalid(u.uShadowMat2.value);
  }

  /** Quantise the ANGLES (then rebuild the vector) so the cache survives between steps. */
  private quantiseLight(lightDir: THREE.Vector3): void {
    _dir.copy(lightDir);
    if (_dir.lengthSq() < 1e-12) _dir.set(0, 1, 0);
    _dir.normalize();
    const el = Math.round(Math.asin(Math.min(1, Math.max(-1, _dir.y))) / LIGHT_STEP);
    const elRad = el * LIGHT_STEP;
    const cosEl = Math.cos(elRad);
    // Azimuth steps widen towards the zenith so a step is ~0.15 degrees of arc
    // everywhere (otherwise a sun passing overhead would re-render constantly).
    const azStep = LIGHT_STEP / Math.max(cosEl, 0.02);
    const az = Math.round(Math.atan2(_dir.z, _dir.x) / azStep);
    const azRad = az * azStep;
    this.qEl = el;
    this.qAz = az;
    this.qDir.set(cosEl * Math.cos(azRad), Math.sin(elRad), cosEl * Math.sin(azRad)).normalize();
  }

  private renderCascade(c: Cascade, scene: THREE.Scene, worldVersion: number, now: number): void {
    const r = this.renderer;
    if (this.lastRenders === 0) {
      this.savedTarget = r.getRenderTarget();
      this.savedCubeFace = r.getActiveCubeFace();
      this.savedMip = r.getActiveMipmapLevel();
      this.savedAutoClear = r.autoClear;
      r.autoClear = false;
    }

    // Light basis from the QUANTISED direction: identical between light steps,
    // so texel snapping below lands on the same grid every time.
    const L = this.qDir;
    _right.crossVectors(Math.abs(L.y) > 0.99 ? AXIS_X : AXIS_Y, L).normalize();
    _up.crossVectors(L, _right);

    // Snap the centre to whole shadow texels in light space (no shimmer when
    // it moves); depth is snapped too so stored depths stay bit-stable.
    const texel = (2 * c.radius) / this.mapSize;
    const sx = Math.round(_camPos.dot(_right) / texel) * texel;
    const sy = Math.round(_camPos.dot(_up) / texel) * texel;
    const sz = Math.round(_camPos.dot(L));
    c.centre.set(0, 0, 0).addScaledVector(_right, sx).addScaledVector(_up, sy).addScaledVector(L, sz);

    // Depth range: every caster between the lowest receiver in the box and the
    // top of the world, along the light. A low sun puts casters very far away,
    // so the reach is capped (nothing that far is loaded anyway).
    const lowY = Math.min(WORLD_HEIGHT, Math.max(0, c.centre.y - c.radius));
    const reach = Math.min(
      MAX_CASTER_REACH,
      Math.max(MIN_CASTER_REACH, (WORLD_HEIGHT - lowY) / Math.max(L.y, MIN_LIGHT_Y)),
    );
    const toLight = c.radius + reach;

    const cam = c.camera;
    cam.near = 0;
    cam.far = toLight + c.radius + BACK_MARGIN;
    cam.updateProjectionMatrix();
    _eye.copy(c.centre).addScaledVector(L, toLight);
    cam.matrix.makeBasis(_right, _up, L).setPosition(_eye);
    cam.matrixWorld.copy(cam.matrix);
    cam.matrixWorldInverse.copy(cam.matrixWorld).invert();

    r.setRenderTarget(c.target);
    // gl.clear honours the depth mask; a previous depthWrite:false draw may have left it off.
    r.state.buffers.depth.setMask(true);
    r.clear(true, true, false);
    r.render(scene, cam);

    // Captured at render time: the cached map keeps using THIS matrix.
    c.matrix.multiplyMatrices(BIAS, cam.projectionMatrix).multiply(cam.matrixWorldInverse);
    c.rendered = true;
    c.keyEl = this.qEl;
    c.keyAz = this.qAz;
    c.worldVersion = worldVersion;
    c.lastRenderMs = now;
    this.lastRenders++;
  }
}

/**
 * GLSL ES 3.0 fragment-shader chunk declaring the uniforms above and
 * `float sampleShadow(vec3 worldPos, vec3 worldNormal, float viewDist)` -> 1 = lit, 0 = shadowed.
 * Requires `#define SHADOW_CASCADES 2|3` to be defined before it (0 / undefined -> always lit).
 * `viewDist` should be the RADIAL camera distance.
 */
export const SHADOW_GLSL = /* glsl */ `
#ifndef SHADOW_CASCADES
#define SHADOW_CASCADES 0
#endif

#if SHADOW_CASCADES > 0

uniform highp sampler2D uShadowMap0;
uniform highp sampler2D uShadowMap1;
uniform highp mat4 uShadowMat0;
uniform highp mat4 uShadowMat1;
#if SHADOW_CASCADES > 2
uniform highp sampler2D uShadowMap2;
uniform highp mat4 uShadowMat2;
#endif
uniform highp vec4 uShadowParams; // x = 1/mapSize, y = max distance, z = fade range, w = cascades

bool shadowInside(highp vec3 c, highp float margin) {
  return c.x > margin && c.y > margin && c.x < 1.0 - margin && c.y < 1.0 - margin
    && c.z > 0.0 && c.z < 1.0;
}

float shadowPcf(highp sampler2D map, highp mat4 m, highp vec3 p, vec3 n) {
  // World size of one shadow texel / depth units per world unit, read back
  // from the matrix rows so biases scale with each cascade's resolution.
  highp float texel = uShadowParams.x / length(vec3(m[0].x, m[1].x, m[2].x));
  highp float zScale = length(vec3(m[0].z, m[1].z, m[2].z));
  // The 3x3 tent kernel below reads one texel further out on every side than
  // the old 2x2 taps did, so the normal offset has to grow with it (1.5 texels
  // left diagonal acne streaks on lit grass tops); a depth bias scaled by the
  // texel covers the remaining slope.
  highp vec3 c = (m * vec4(p + n * (texel * 2.5), 1.0)).xyz;
  highp float z = c.z - (0.01 + 1.0 * texel) * zScale;
  // Smooth PCF: 3x3 texel neighbourhood with a tent weighting driven by the
  // fractional texel position, so edges blend instead of stepping per texel.
  highp float size = 1.0 / uShadowParams.x;
  highp vec2 t = c.xy * size - 0.5;
  highp vec2 base = floor(t);
  highp vec2 f = t - base;
  highp vec3 wx = vec3(1.0 - f.x, 1.0, f.x);
  highp vec3 wy = vec3(1.0 - f.y, 1.0, f.y);
  float lit = 0.0;
  for (int j = 0; j < 3; j++) {
    for (int i = 0; i < 3; i++) {
      highp vec2 uv = (base + vec2(float(i), float(j)) + 0.5) * uShadowParams.x;
      lit += wx[i] * wy[j] * step(z, textureLod(map, uv, 0.0).r);
    }
  }
  return lit * 0.25;
}

float sampleShadow(vec3 worldPos, vec3 worldNormal, float viewDist) {
  if (viewDist >= uShadowParams.y) return 1.0;

  highp vec3 p = worldPos + worldNormal * 0.03;

  // Cached cascades are not centred on the camera: pick the first whose map
  // actually contains the point (margin covers normal offset + PCF taps).
  highp float margin = 3.0 * uShadowParams.x;
  float lit = 1.0;
  if (shadowInside((uShadowMat0 * vec4(p, 1.0)).xyz, margin)) {
    lit = shadowPcf(uShadowMap0, uShadowMat0, p, worldNormal);
  } else if (shadowInside((uShadowMat1 * vec4(p, 1.0)).xyz, margin)) {
    lit = shadowPcf(uShadowMap1, uShadowMat1, p, worldNormal);
  }
#if SHADOW_CASCADES > 2
  else if (shadowInside((uShadowMat2 * vec4(p, 1.0)).xyz, margin)) {
    lit = shadowPcf(uShadowMap2, uShadowMat2, p, worldNormal);
  }
#endif

  float fade = smoothstep(uShadowParams.y - uShadowParams.z, uShadowParams.y, viewDist);
  return mix(lit, 1.0, fade);
}

#else

float sampleShadow(vec3 worldPos, vec3 worldNormal, float viewDist) {
  return 1.0;
}

#endif
`;
